import { useMemo, useRef, useState } from 'react';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { JartinetHermandad } from '@/lib/types';
import { cn } from '@/lib/utils';
import { AtSign } from 'lucide-react';

interface MentionProfileOption {
    type: 'hermandad' | 'banda' | 'organizacion';
    id: string;
    name: string;
    escudoUrl?: string;
}

/**
 * RichTextarea con autocompletado de menciones tipo Instagram. Al escribir `@`
 * aparece un desplegable de cuentas (hermandades, bandas, organizaciones).
 * Al elegir, inserta el token `@[Nombre](tipo:id)` que luego `SocialText`
 * renderiza como chip interactivo. Compatible con FormData (usa `name`).
 */
export function MentionTextarea({
    name,
    defaultValue,
    placeholder,
    id,
    className,
}: {
    name: string;
    defaultValue?: string;
    placeholder?: string;
    id?: string;
    className?: string;
}) {
    const { jartinetData } = useAuth();
    const ref = useRef<HTMLTextAreaElement | null>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [atIndex, setAtIndex] = useState<number | null>(null);
    const [activeIdx, setActiveIdx] = useState(0);

    const allProfiles: MentionProfileOption[] = useMemo(() => {
        const h = (jartinetData?.hermandades || []).map((p: JartinetHermandad) => ({ type: 'hermandad' as const, id: p.id, name: p.nombreCompleto || p.nombre, escudoUrl: p.escudoUrl }));
        const b = (jartinetData?.bandas || []).map((p: any) => ({ type: 'banda' as const, id: p.id, name: p.nombreCompleto, escudoUrl: p.escudoUrl }));
        const o = (jartinetData?.organizaciones || []).map((p: any) => ({ type: 'organizacion' as const, id: p.id, name: p.nombreCompleto, escudoUrl: p.escudoUrl }));
        return [...h, ...b, ...o].filter(p => p.name);
    }, [jartinetData]);

    const matches = useMemo(() => {
        if (!open) return [];
        const q = query.toLowerCase();
        return allProfiles.filter(p => p.name.toLowerCase().includes(q)).slice(0, 6);
    }, [open, query, allProfiles]);

    const detectTrigger = (el: HTMLTextAreaElement) => {
        const caret = el.selectionStart ?? el.value.length;
        const before = el.value.slice(0, caret);
        const m = before.match(/(?:^|\s)@(\S{0,40})$/);
        if (m) {
            setQuery(m[1]);
            setAtIndex(caret - m[1].length - 1);
            setOpen(true);
            setActiveIdx(0);
        } else {
            setOpen(false);
            setAtIndex(null);
        }
    };

    const insertMention = (p: MentionProfileOption) => {
        const el = ref.current;
        if (!el || atIndex === null) return;
        const caret = el.selectionStart ?? el.value.length;
        const cleanName = p.name.replace(/[[\]()]/g, ' ').replace(/\s+/g, ' ').trim();
        const token = `@[${cleanName}](${p.type}:${p.id}) `;
        const newValue = el.value.slice(0, atIndex) + token + el.value.slice(caret);

        const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
        setter?.call(el, newValue);
        el.dispatchEvent(new Event('input', { bubbles: true }));

        const newCaret = atIndex + token.length;
        el.focus();
        el.setSelectionRange(newCaret, newCaret);
        setOpen(false);
        setAtIndex(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!open || matches.length === 0) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => (i + 1) % matches.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => (i - 1 + matches.length) % matches.length); }
        else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(matches[activeIdx]); }
        else if (e.key === 'Escape') { setOpen(false); }
    };

    return (
        <div className="relative">
            <RichTextarea
                ref={ref}
                id={id}
                name={name}
                defaultValue={defaultValue}
                placeholder={placeholder}
                className={className}
                onInput={(e) => detectTrigger(e.currentTarget)}
                onClick={(e) => detectTrigger(e.currentTarget)}
                onKeyUp={(e) => detectTrigger(e.currentTarget)}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
            {open && matches.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-xl border border-white/10 bg-popover/95 p-1 shadow-2xl backdrop-blur-md">
                    <p className="flex items-center gap-1 px-2 py-1 text-[0.62rem] font-bold uppercase tracking-wider text-muted-foreground/60"><AtSign className="h-3 w-3" /> Mencionar cuenta</p>
                    {matches.map((p, i) => (
                        <button
                            key={`${p.type}:${p.id}`}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); insertMention(p); }}
                            onMouseEnter={() => setActiveIdx(i)}
                            className={cn('flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors', i === activeIdx ? 'bg-primary/15' : 'hover:bg-white/5')}
                        >
                            <Avatar className="h-7 w-7 border border-white/10">
                                <AvatarImage src={p.escudoUrl} />
                                <AvatarFallback className="text-[0.6rem]">{p.name.substring(0, 2)}</AvatarFallback>
                            </Avatar>
                            <span className="flex flex-col min-w-0">
                                <span className="truncate text-sm font-medium">{p.name}</span>
                                <span className="text-[0.6rem] capitalize text-muted-foreground">{p.type}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
